import Card from '@/components/Card';
import { Datatable } from '@/components/Datatable/Datatable';
import { useEffect, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { TableViewProps } from '@/type/listing.types';
import { TableTitle } from '@/components/Header/TableTitle';
import Loader from '@/components/Loading/Loader';
import { RichSelect } from '@/components/Controls/RichSelect';
import { ClearButton } from '@/components/Custom/Button/ClearButton';
import { useRepairCostingFilter } from '@/hooks/filters/repair-costing-filter.hooks';
import { useGetEquipmentsSelectOptions } from '@/hooks/equipment.hooks';
import { useGetRepairServicesSelectOptions } from '@/hooks/repair-service.hooks';
import { useGetUniqueDamageNamesSelectOptions } from '@/hooks/damage.hooks';
import {
  AdvancedFilterPanel,
  AdvancedFilterToggle,
  FilterField,
  FilterGrid,
  ToneIcon,
  getStatusPillOptions,
} from '@/components/Custom/Filter/FilterKit';
import { usePageSize } from '@/hooks/page-size.hooks';
import { COSTINGS_TIPS } from '@/docs/tips/costings';
import {
  IconAntennaBars1,
  IconAntennaBars3,
  IconAntennaBars5,
  IconCar,
  IconCarCrash,
  IconGauge,
  IconHammer,
  IconToggleRight,
} from '@tabler/icons-react';

const toolbarSelectClassName =
  'w-auto min-w-[190px] max-1.5xl:min-w-0 max-1.5xl:flex-1 max-sm:basis-full';

export const RepairCostingTableView = <TSchema extends FieldValues>({
  t,
  useGetAllData,
  onGetTotalActive,
  tableHeaders: TableHeaders,
  tableData: TableData,
  pendingDeactivate,
  onDeactivate,
}: TableViewProps<TSchema>) => {
  const [totalPages, setTotalPages] = useState<number>();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const { pageSize: limit, setPageSize } = usePageSize();
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  /**
   * === Filter Section ===
   */
  const {
    filtersEmpty,
    clearFilters,
    equipmentId,
    damageName,
    damageSeverity,
    repairServiceId,
    status,
    searchByEquipmentId,
    searchByDamageName,
    searchByDamageSeverity,
    searchByRepairServiceId,
    searchByStatus,
  } = useRepairCostingFilter({
    setCurrentPage,
  });
  const advancedFiltersCount = [status].filter(Boolean).length;
  const severityOptions = [
    {
      label: `${t('common:level')} 1`,
      value: '1',
      icon: <IconAntennaBars1 size={16} className="text-amber-500" />,
    },
    {
      label: `${t('common:level')} 2`,
      value: '2',
      icon: <IconAntennaBars3 size={16} className="text-orange-500" />,
    },
    {
      label: `${t('common:level')} 3`,
      value: '3',
      icon: <IconAntennaBars5 size={16} className="text-rose-500" />,
    },
  ];
  /**
   * === END OF Filter Section ===
   */

  /**
   * === Data Fetching section ===
   */
  const {
    data: allData,
    isLoading: isLoadingAllData,
    isPending: isPendingAllData,
  } = useGetAllData(
    {
      equipmentId,
      damageName,
      damageSeverity,
      repairServiceId,
      status,
    },
    currentPage,
    limit,
    sortColumn,
    sortDirection,
  );

  const { getAllEquipmentsSelectOptions, isLoadingAllEquipments } =
    useGetEquipmentsSelectOptions();

  const { getAllDamageNamesSelectOptions, isLoadingAllDamages } =
    useGetUniqueDamageNamesSelectOptions();

  const { getAllRepairServicesSelectOptions, isLoadingAllRepairServices } =
    useGetRepairServicesSelectOptions();

  /**
   * === END OF Data Fetching section ===
   */

  /**
   * === Pagination Section ===
   */
  useEffect(() => {
    setTotalPages(allData?.data?.totalPages);
    onGetTotalActive?.(allData?.data?.['total']);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allData]);
  /**
   * === END OF Pagination Section ===
   */

  /**
   * === Sorting Section ===
   */
  const handleSort = (column: string) => {
    setSortColumn(column);
    setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  };
  /**
   * === END OF Sorting Section ===
   */

  return (
    <>
      <Card bodyClassName="flex flex-col gap-5">
        <div className="flex flex-wrap justify-between gap-3.5">
          <div className="flex gap-3.5 max-1.5xl:w-full max-1.5xl:justify-between">
            <TableTitle
              textClassName="mt-1.5"
              text={t('common:costings')}
              infoTip={COSTINGS_TIPS.repairCostingsTable}
            />
            <div className="hidden max-md:block">
              <ClearButton disabled={filtersEmpty} onClick={clearFilters} />
            </div>
          </div>
          <div className="flex flex-wrap gap-3.5 ml-auto max-1.5xl:ml-0 max-1.5xl:w-full">
            <RichSelect
              searchable
              containerClassName={toolbarSelectClassName}
              leftIcon={<ToneIcon icon={IconCar} tone="sky" size="sm" />}
              placeholder={t('common:allEquipments')}
              aria-label={t('common:equipment')}
              loading={isLoadingAllEquipments}
              options={getAllEquipmentsSelectOptions() || []}
              value={equipmentId ?? ''}
              onValueChange={searchByEquipmentId}
            />
            <RichSelect
              searchable
              containerClassName={toolbarSelectClassName}
              leftIcon={<ToneIcon icon={IconCarCrash} tone="rose" size="sm" />}
              placeholder={t('common:allDamages')}
              aria-label={t('common:damage')}
              loading={isLoadingAllDamages}
              options={getAllDamageNamesSelectOptions() || []}
              value={damageName ?? ''}
              onValueChange={searchByDamageName}
            />
            <RichSelect
              containerClassName={toolbarSelectClassName}
              leftIcon={<ToneIcon icon={IconGauge} tone="orange" size="sm" />}
              placeholder={t('common:allSeverities')}
              aria-label={t('common:severity')}
              options={severityOptions}
              value={damageSeverity ?? ''}
              onValueChange={searchByDamageSeverity}
            />
            <RichSelect
              searchable
              containerClassName={toolbarSelectClassName}
              leftIcon={<ToneIcon icon={IconHammer} tone="violet" size="sm" />}
              placeholder={t('common:allServices')}
              aria-label={t('common:repairService')}
              loading={isLoadingAllRepairServices}
              options={getAllRepairServicesSelectOptions() || []}
              value={repairServiceId ?? ''}
              onValueChange={searchByRepairServiceId}
            />
            <div className="flex justify-end max-sm:w-full">
              <AdvancedFilterToggle
                open={showAdvancedFilters}
                activeCount={advancedFiltersCount}
                onToggle={() => setShowAdvancedFilters((prev) => !prev)}
              />
            </div>
            <div className="hidden max-1.5xl:block max-md:hidden">
              <ClearButton disabled={filtersEmpty} onClick={clearFilters} />
            </div>
            <div className="max-1.5xl:hidden">
              <ClearButton disabled={filtersEmpty} onClick={clearFilters} />
            </div>
          </div>
        </div>
        <AdvancedFilterPanel open={showAdvancedFilters}>
          <FilterGrid>
            <FilterField
              icon={IconToggleRight}
              tone="emerald"
              label={t('common:status')}
            >
              <RichSelect
                placeholder={t('common:allStates')}
                options={getStatusPillOptions(
                  t('common:active'),
                  t('common:inactive'),
                )}
                value={status ?? ''}
                onValueChange={searchByStatus}
              />
            </FilterField>
          </FilterGrid>
        </AdvancedFilterPanel>
        <div>
          <Datatable
            t={t}
            headers={<TableHeaders t={t} onSort={handleSort} />}
            data={
              isLoadingAllData ? (
                <></>
              ) : !allData?.data?.data?.length ? null : (
                <TableData
                  t={t}
                  data={allData?.data?.data}
                  pendingDeactivate={pendingDeactivate}
                  onDeactivate={onDeactivate}
                />
              )
            }
            showPagination={allData?.data?.data?.length > 0 && totalPages > 1}
            totalPages={totalPages}
            currentPage={currentPage}
            onPageChange={(page) => setCurrentPage(page)}
            totalItems={isLoadingAllData ? undefined : allData?.data?.['total']}
            pageSize={limit}
            onPageSizeChange={setPageSize}
          />
        </div>
      </Card>
      {(isLoadingAllData || isPendingAllData || pendingDeactivate) && (
        <Loader />
      )}
    </>
  );
};
